import { Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  setSelfFormFillingScoreSettings,
  useReviewCreationData,
  useSelfFormFillingScoreSettings,
} from 'reducers/reviewCreationSlice';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Accordion } from 'uiToolkitV1/Accordion';
import { AccordionButton, AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { SelectionGridTable } from 'uiToolkitV1/SelectionGridTable';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextHeader4 } from 'uiToolkitV1/Typography';

import { useTemplateMetadata } from '../../Hooks/dataHooks/formFillingsDataHooks';
import { PreviewFormButton } from './PreviewFormButton';

export const SelfReviewerSettings = ({ feedbackFormId }: { feedbackFormId: number }) => {
  const { reviewCycleId } = useReviewCreationData();
  const dispatch = useDispatch();
  const selfFormFillingScoreSettings = useSelfFormFillingScoreSettings({ feedbackFormId });
  const selfTemplateMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.SELF,
  });

  const templateHasQuestions = selfTemplateMetadata?.template?.questionCount > 0;

  const hasGoalQuestions = selfTemplateMetadata?.template?.templateSetting?.overallRatingDistribution?.PERFORMANCE > 0;
  const hasCompetencyQuestions =
    selfTemplateMetadata?.template?.templateSetting?.overallRatingDistribution?.POTENTIAL > 0;

  const tableHeaders = [];
  if (hasGoalQuestions) tableHeaders.push('Overall Goals Score');
  if (hasCompetencyQuestions) tableHeaders.push('Overall Competency Score');
  tableHeaders.push('Overall Score');

  const tableOptions = [];
  if (hasGoalQuestions) {
    tableOptions.push({
      label: 'Show',
      checked: selfFormFillingScoreSettings?.isShowGoalRating,
    });
  }
  if (hasCompetencyQuestions) {
    tableOptions.push({
      label: 'Show',
      checked: selfFormFillingScoreSettings?.isShowCompetencyRating,
    });
  }
  tableOptions.push({
    label: 'Show',
    checked: selfFormFillingScoreSettings?.isShowOverallRating,
  });

  const onChangeHandlers = [];
  if (hasGoalQuestions) {
    onChangeHandlers.push((checked: boolean) =>
      dispatch(
        setSelfFormFillingScoreSettings({
          feedbackFormId,
          settings: { isShowGoalRating: checked },
        })
      )
    );
  }
  if (hasCompetencyQuestions) {
    onChangeHandlers.push((checked: boolean) =>
      dispatch(
        setSelfFormFillingScoreSettings({
          feedbackFormId,
          settings: { isShowCompetencyRating: checked },
        })
      )
    );
  }
  onChangeHandlers.push((checked: boolean) =>
    dispatch(
      setSelfFormFillingScoreSettings({
        feedbackFormId,
        settings: { isShowOverallRating: checked },
      })
    )
  );

  const handleChange = (checked: boolean, i: number, j: number) => {
    onChangeHandlers[j]?.(checked);
  };

  return (
    <Accordion defaultIndex={0} allowMultiple allowToggle boxShadow="none !important">
      <AccordionItem2 sxProps={{ borderBottom: 'none !important' }}>
        <AccordionButton>
          <Flex justifyContent="space-between" alignItems="center" flexGrow={1}>
            <TextHeader4 bold>Self reviewer</TextHeader4>
            <PreviewFormButton
              feedbackFormId={feedbackFormId}
              providerType={REVIEW_PROVIDER_TYPES.SELF}
              isDisabled={!templateHasQuestions}
            />
          </Flex>
        </AccordionButton>
        <AccordionPanel2 pl="32px !important" mb="10px">
          <Flex flexDir="column" gap={3}>
            <Flex alignItems="center" gap={2}>
              <TextBodyMedium>Which scores can the self see before submitting the review forms?</TextBodyMedium>
              <TooltipInfoV1 title="When reviewing the form, the self reviewer can see the average score for each section on the left and at the top of the page" />
            </Flex>
            <SelectionGridTable headers={tableHeaders} rows={[tableOptions]} onChange={handleChange} />
          </Flex>
        </AccordionPanel2>
      </AccordionItem2>
    </Accordion>
  );
};
